import { PostsQuery } from '@/graphql/generated/posts.generated';
import { ArrayElement } from '@/utils/types/array-element';
import { Maybe, Timezone } from '@/graphql/generated/types.generated';
import {
    Box,
    Card,
    CardContent,
    colors,
    Stack,
    styled,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { formatPostDate } from '../../utils/format-post-date.util';
import PostStageAvatar from '../../post/PostStageAvatar';
import PostTypeIcon from '../../post/PostTypeIcon';
import { useTranslation } from 'react-i18next';
import PostImage from '@/components/post-planning/post/PostImage';
import { useSession } from '@/providers/session-provider';
import PostScheduleDateTimezoneAlert from '../../post/PostScheduleTimezoneAlert';
import markdownToPlainText from '@/utils/markdown-to-plain-text';
import { LightbulbRounded } from '@mui/icons-material';

export const CalendarPostPreviewTooltip: React.ComponentType<TooltipProps> = styled(
    ({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />,
)(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: 'transparent',
        width: 300,
    },
}));

type CalendarPostPreviewProps = {
    post: ArrayElement<PostsQuery['posts']['items']>;
    publicView?: boolean;
    workspaceTimezone?: Maybe<Timezone> | string;
    userTimezone?: Maybe<Timezone> | string;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
};

const CalendarPostPreviewContent = ({
    post,
    onClick,
    publicView,
    workspaceTimezone,
    userTimezone,
}: CalendarPostPreviewProps) => {
    const { t } = useTranslation();

    return (
        <Card
            onClick={(e) => {
                e.stopPropagation();
                if (onClick) {
                    onClick(e);
                }
            }}
            sx={{
                width: 300,
                borderRadius: 2,
                border: 1,
                borderColor: '#C5C7DA',
                ...(onClick && { cursor: 'pointer' }),
            }}
            elevation={8}
            variant="elevation"
        >
            <CardContent
                sx={{
                    ':last-child': { p: 2 },
                }}
            >
                <Stack gap={2}>
                    <Stack gap={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            {post.nets && post.nets.length > 0 && (
                                <Stack direction="row" gap={0.5}>
                                    {post.nets?.map((net, key) => (
                                        <Tooltip
                                            key={key}
                                            title={
                                                <Stack>
                                                    {post.profiles
                                                        ?.filter(
                                                            (p) =>
                                                                p.netProfileAuth?.netProfile?.net?.code ===
                                                                net.code,
                                                        )
                                                        .slice(0, 4)
                                                        .map((p) => (
                                                            <Typography
                                                                key={p.netProfileAuth?.netProfile?.name}
                                                                fontSize={10}
                                                            >
                                                                {p.netProfileAuth?.netProfile?.name}
                                                            </Typography>
                                                        ))}
                                                    {(post.profiles || []).length > 4 && (
                                                        <Typography fontSize={10}>
                                                            {t('str.and-more', {
                                                                count: post.profiles?.slice(4).length,
                                                            })}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            }
                                        >
                                            <Box
                                                component="img"
                                                src={`/social/${net.code.toLowerCase()}.svg`}
                                                height={18}
                                                width={18}
                                            />
                                        </Tooltip>
                                    ))}
                                </Stack>
                            )}
                        </Stack>

                        {/* A dateless post has no time to show, and no timezone to warn about */}
                        {post.publishAt && (
                            <Stack direction="row" gap={1} alignItems="center">
                                <Typography variant="caption" lineHeight={0}>
                                    {formatPostDate(
                                        post.publishAt,
                                        typeof workspaceTimezone === 'string'
                                            ? workspaceTimezone
                                            : workspaceTimezone?.timezone,
                                        true,
                                    )}
                                </Typography>
                                {!publicView && (
                                    <PostScheduleDateTimezoneAlert
                                        date={post.publishAt}
                                        userTimezone={
                                            typeof userTimezone === 'string' ? undefined : userTimezone
                                        }
                                        workspaceTimezone={
                                            typeof workspaceTimezone === 'string'
                                                ? undefined
                                                : workspaceTimezone
                                        }
                                        isTooltip
                                    />
                                )}
                            </Stack>
                        )}

                        <Typography
                            variant="caption"
                            color="textSecondary"
                            whiteSpace="pre-line"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                            }}
                        >
                            {post.previewPostBody?.plainText ?? (
                                <i style={{ color: colors.grey[600] }}>
                                    {post.briefing?.markdown && (
                                        <span>
                                            <LightbulbRounded
                                                sx={{
                                                    fontSize: 12,
                                                    color: colors.yellow[400],
                                                }}
                                            />{' '}
                                            {markdownToPlainText(post.briefing.markdown).replace(/\n/g, ' ')}
                                        </span>
                                    )}
                                </i>
                            )}
                        </Typography>
                    </Stack>

                    <Stack gap={1}>
                        <PostImage
                            type={post.type}
                            previewMediaLib={post.previewMediaLib}
                            variant="calendar-preview"
                        />
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <PostStageAvatar stage={post.stage} chip />
                            <PostTypeIcon type={post.type} />
                        </Stack>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

const PrivateCalendarPostPreview = ({
    post,
    onClick,
}: {
    post: ArrayElement<PostsQuery['posts']['items']>;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}) => {
    const {
        me: { timezone: myTimezone },
        workspace: { timezone: wTz },
    } = useSession();

    return (
        <CalendarPostPreviewContent
            post={post}
            workspaceTimezone={wTz}
            userTimezone={myTimezone}
            onClick={onClick}
        />
    );
};

const PublicCalendarPostPreview = ({
    post,
    onClick,
}: {
    post: ArrayElement<PostsQuery['posts']['items']>;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}) => {
    const timezone = dayjs.tz.guess();
    return (
        <CalendarPostPreviewContent
            post={post}
            publicView
            workspaceTimezone={timezone}
            userTimezone={timezone}
            onClick={onClick}
        />
    );
};

const CalendarPostPreview = ({
    post,
    publicView,
    onClick,
}: {
    post: ArrayElement<PostsQuery['posts']['items']>;
    publicView?: boolean;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}) => {
    return publicView ? (
        <PublicCalendarPostPreview post={post} onClick={onClick} />
    ) : (
        <PrivateCalendarPostPreview post={post} onClick={onClick} />
    );
};

export default CalendarPostPreview;
